<template>
  <div class="mini-overlay" @keydown.esc="close" tabindex="0" ref="root">
    <div class="mini-card">
      <canvas ref="canvas" :width="width" :height="height" class="snake-canvas"></canvas>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SnakeGame',
  props: {
    targetScore: { type: Number, default: 10 },
    width: { type: Number, default: 800 },
    height: { type: Number, default: 800 },
  },
  emits: ['done', 'close'],
  data() {
    return {
      ctx: null,
      scale: 50,
      snake: [{ x: 5, y: 5 }],
      dir: { x: 1, y: 0 },
      food: null,
      score: 0,
      running: false,
      lastTime: 0,
      tickInterval: 100, // ms
      accumulated: 0,
      touchStart: null,
    };
  },
  mounted() {
    const cvs = this.$refs.canvas;
    this.ctx = cvs.getContext('2d');
    this.spawnFood();
    this.start();
    // focus for keyboard
    this.$refs.root.focus();
    window.addEventListener('keydown', this.onKey);
    // touch support
    cvs.addEventListener('pointerdown', this.onPointerDown);
    cvs.addEventListener('pointerup', this.onPointerUp);
    requestAnimationFrame(this.loop);
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.onKey);
    const cvs = this.$refs.canvas;
    cvs.removeEventListener('pointerdown', this.onPointerDown);
    cvs.removeEventListener('pointerup', this.onPointerUp);
  },
  methods: {
    start() {
      this.running = true;
      this.score = 0;
      this.snake = [{ x: 5, y: 5 }];
      this.dir = { x: 1, y: 0 };
      this.spawnFood();
      this.accumulated = 0;
      this.lastTime = performance.now();
    },
    loop(now) {
      const dt = now - this.lastTime;
      this.lastTime = now;
      this.accumulated += dt;
      while (this.accumulated >= this.tickInterval) {
        this.update();
        this.accumulated -= this.tickInterval;
      }
      this.draw();
      if (this.running) requestAnimationFrame(this.loop);
    },
    update() {
      // move snake
      const head = { x: this.snake[0].x + this.dir.x, y: this.snake[0].y + this.dir.y };
      // wrap
      head.x = (head.x + (this.width / this.scale)) % (this.width / this.scale);
      head.y = (head.y + (this.height / this.scale)) % (this.height / this.scale);

      // collision with self
      if (this.snake.some(s => s.x === head.x && s.y === head.y)) {
        this.running = false;
        return;
      }

      this.snake.unshift(head);

      // eat
      if (head.x === this.food.x && head.y === this.food.y) {
        this.score++;
        this.spawnFood();
        if (this.score >= this.targetScore) {
          this.finish();
        }
      } else {
        this.snake.pop();
      }
    },
    draw() {
      const ctx = this.ctx;
      ctx.clearRect(0, 0, this.width, this.height);
      // food
      ctx.fillStyle = '#e53e3e';
      ctx.fillRect(this.food.x * this.scale, this.food.y * this.scale, this.scale, this.scale);
      // snake
      ctx.fillStyle = '#34d399';
      this.snake.forEach((s) => {
        ctx.fillRect(s.x * this.scale + 1, s.y * this.scale + 1, this.scale - 2, this.scale - 2);
      });
    },
    spawnFood() {
      const gw = this.width / this.scale;
      const gh = this.height / this.scale;
      let x, y;
      do {
        x = Math.floor(Math.random() * gw);
        y = Math.floor(Math.random() * gh);
      } while (this.snake.some(s => s.x === x && s.y === y));
      this.food = { x, y };
    },
    onKey(e) {
      const k = e.key;
      if (k === 'ArrowUp' && this.dir.y === 0) this.dir = { x: 0, y: -1 };
      if (k === 'ArrowDown' && this.dir.y === 0) this.dir = { x: 0, y: 1 };
      if (k === 'ArrowLeft' && this.dir.x === 0) this.dir = { x: -1, y: 0 };
      if (k === 'ArrowRight' && this.dir.x === 0) this.dir = { x: 1, y: 0 };
      if (k === ' ' && !this.running) { this.start(); requestAnimationFrame(this.loop); }
    },
    onPointerDown(e) {
      this.touchStart = { x: e.clientX, y: e.clientY };
    },
    onPointerUp(e) {
      if (!this.touchStart) return;
      const dx = e.clientX - this.touchStart.x;
      const dy = e.clientY - this.touchStart.y;
      if (Math.abs(dx) > Math.abs(dy)) {
        if (dx > 10 && this.dir.x === 0) this.dir = { x: 1, y: 0 };
        if (dx < -10 && this.dir.x === 0) this.dir = { x: -1, y: 0 };
      } else {
        if (dy > 10 && this.dir.y === 0) this.dir = { x: 0, y: 1 };
        if (dy < -10 && this.dir.y === 0) this.dir = { x: 0, y: -1 };
      }
      this.touchStart = null;
    },
    finish() {
      this.running = false;
      this.$emit('done', this.score);
    },
    close() {
      this.running = false;
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
.mini-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0,0,0,0.5);
  z-index: 15000;
}
.mini-card {
  background: white;
  border-radius: 10px;
  padding: 8px;
  width: fit-content;
  max-width: 95%;
  box-shadow: 0 10px 30px rgba(0,0,0,0.4);
}
.mini-header {
  display:flex;
  justify-content: space-between;
  align-items:center;
  margin-bottom:6px;
}
.snake-canvas {
  background: #0f172a;
  display:block;
}
.mini-footer { text-align:center; margin-top:6px; font-size:13px; color:#555 }
</style>